import { getApiBaseUrl } from '../utils/urlConfig';
import { DEBUG_CONNECTIVITY, DEBUG_LOGS, DEBUG_VERBOSE_API } from '../utils/debugFlags';

export interface HealthCheckResponse {
  status: string;
  message?: string;
  timestamp: string;
}

export interface HealthCheckResult {
  success: boolean;
  responseTime: number;
  error?: string;
  data?: HealthCheckResponse;
}

class HealthCheckService {
  private readonly baseUrl: string;
  private readonly primaryHealthEndpoint: string;
  
  constructor() {
    const apiBaseUrl = getApiBaseUrl();
    try {
      const parsed = new URL(apiBaseUrl);
      this.baseUrl = parsed.origin;
    } catch {
      this.baseUrl = apiBaseUrl.replace(/\/api\/?$/, '');
    }
    const normalizedApiBase = apiBaseUrl.replace(/\/$/, '');
    this.primaryHealthEndpoint = `${normalizedApiBase}/health/`;

    if (DEBUG_VERBOSE_API) {
      console.log('[HealthCheck] Initialized:', this.primaryHealthEndpoint);
    }
  }
  
  // Fallback endpoints for when primary is unavailable (temporarily disabled to prevent unnecessary requests)
  private readonly fallbackEndpoints: string[] = [];
  private readonly timeout = 5000; // 5 seconds
  private abortController?: AbortController;
  private workingEndpoint: string | null = null;
  /** Only log when health state changes (avoids spamming every 10s poll) */
  private lastLoggedSuccess: boolean | null = null;

  /**
   * Log only when health state changes (debug mode), to avoid console spam on 10s polls.
   */
  private logHealthStateChange(result: HealthCheckResult): void {
    if (!DEBUG_CONNECTIVITY) return;
    if (this.lastLoggedSuccess === result.success) return;
    this.lastLoggedSuccess = result.success;
    if (result.success) {
      console.log('✅ Health check: backend reachable');
    } else {
      console.log('❌ Health check: backend unreachable', result.error || '');
    }
  }

  /**
   * Perform a health check against the backend
   * @param useCache - Whether to use cache-busting parameters
   * @returns Promise with health check result
   */
  async checkHealth(useCache: boolean = false): Promise<HealthCheckResult> {
    // Always try primary endpoint first (it's fastest and doesn't require auth)
    const primaryResult = await this.tryEndpoint(this.primaryHealthEndpoint, useCache);
    
    if (primaryResult.success) {
      this.workingEndpoint = this.primaryHealthEndpoint;
      this.logHealthStateChange(primaryResult);
      return primaryResult;
    }
    
    // If we have a different known working endpoint, try it
    if (this.workingEndpoint && this.workingEndpoint !== this.primaryHealthEndpoint) {
      const workingResult = await this.tryEndpoint(this.workingEndpoint, useCache);
      if (workingResult.success) {
        if (DEBUG_CONNECTIVITY) {
          console.log('[HealthCheck] Primary failed, known working endpoint succeeded');
        }
        this.logHealthStateChange(workingResult);
        return workingResult;
      }
      this.workingEndpoint = null;
    }
    
    // If primary fails, try fallback endpoints as last resort
    for (const endpoint of this.fallbackEndpoints) {
      const result = await this.tryEndpoint(endpoint, useCache);
      if (result.success) {
        this.workingEndpoint = endpoint;
        this.logHealthStateChange(result);
        return result;
      }
    }

    const failedResult: HealthCheckResult = {
      success: false,
      responseTime: 0,
      error: 'All health endpoints unavailable - backend may be down',
    };
    this.logHealthStateChange(failedResult);
    return failedResult;
  }

  /**
   * Try a single health endpoint
   */
  private async tryEndpoint(endpoint: string, useCache: boolean): Promise<HealthCheckResult> {
    const startTime = performance.now();
    let timeoutId: NodeJS.Timeout | undefined;

    try {
      // Add cache-busting parameter if requested
      const url = useCache 
        ? `${endpoint}?_t=${Date.now()}` 
        : endpoint;

      // Create a browser-compatible timeout signal
      const abortController = new AbortController();
      timeoutId = setTimeout(() => {
        abortController.abort();
      }, 3000); // 3 second timeout

      const response = await fetch(url, {
        method: 'GET',
        cache: 'no-cache',
        headers: {
          'Accept': 'application/json',
        },
        // Health check doesn't need credentials - it's a public endpoint
        credentials: 'omit',
        // Browser-compatible timeout using AbortController
        signal: abortController.signal
      });

      // Clear the timeout since the request completed
      clearTimeout(timeoutId);

      const responseTime = performance.now() - startTime;

      if (response.ok) {
        let data: HealthCheckResponse;
        
        try {
          data = await response.json();
        } catch (jsonError) {
          if (DEBUG_LOGS) {
            console.warn('[HealthCheck] JSON parse failed:', jsonError);
          }
          // If JSON parsing fails, create a basic response
          data = {
            status: 'healthy',
            message: 'System operational',
            timestamp: new Date().toISOString(),
          };
        }

        return {
          success: true,
          responseTime: Math.round(responseTime),
          data,
        };
      } else {
        return {
          success: false,
          responseTime: Math.round(responseTime),
          error: `HTTP ${response.status}: ${response.statusText}`,
        };
      }
    } catch (error: any) {
      const responseTime = performance.now() - startTime;
      
      // Clear timeout in case of error
      if (typeof timeoutId !== 'undefined') {
        clearTimeout(timeoutId);
      }
      
      // Provide more specific error messages for development
      let errorMessage = error.message || 'Network error';
      if (error.name === 'AbortError') {
        errorMessage = 'Request timeout (3s)';
      } else if (error.message?.includes('fetch')) {
        errorMessage = 'Connection failed - backend may be down';
      }

      return {
        success: false,
        responseTime: Math.round(responseTime),
        error: errorMessage,
      };
    }
  }

  /**
   * Perform a lightweight connectivity test using HEAD request
   * @returns Promise with connectivity result
   */
  async testConnectivity(): Promise<boolean> {
    try {
      this.cancelPendingRequest();
      this.abortController = new AbortController();

      // Use the working endpoint if we have one, otherwise try the primary endpoint
      const endpoint = this.workingEndpoint || this.primaryHealthEndpoint;

      const response = await fetch(endpoint, {
        method: 'HEAD',
        cache: 'no-cache',
        signal: this.abortController?.signal,
        credentials: 'omit',
      });

      return response.ok;
    } catch (error) {
      if (DEBUG_CONNECTIVITY) {
        console.debug('[HealthCheck] Connectivity test failed:', error);
      }
      return false;
    }
  }

  /**
   * Cancel any pending health check request
   */
  cancelPendingRequest(): void {
    if (this.abortController) {
      this.abortController.abort();
      this.abortController = undefined;
    }
  }

  /**
   * Cleanup method to cancel any ongoing requests
   */
  cleanup(): void {
    this.cancelPendingRequest();
  }

  /**
   * Get the currently working endpoint for debugging
   */
  getWorkingEndpoint(): string | null {
    return this.workingEndpoint;
  }

  /**
   * Manually set a working endpoint for testing
   */
  setWorkingEndpoint(endpoint: string): void {
    const allEndpoints = [this.primaryHealthEndpoint, ...this.fallbackEndpoints];
    if (allEndpoints.includes(endpoint)) {
      this.workingEndpoint = endpoint;
    }
  }

  /**
   * Get all available health endpoints
   */
  getAvailableEndpoints(): string[] {
    return [this.primaryHealthEndpoint, ...this.fallbackEndpoints];
  }
}

// Export singleton instance
export const healthCheckService = new HealthCheckService();

// Export to window for debugging in browser console
if (typeof window !== 'undefined') {
  (window as any).healthCheckService = healthCheckService;
  (window as any).testHealthCheck = async () => {
    if (DEBUG_VERBOSE_API) {
      console.log('[HealthCheck] Testing from console...');
    }
    const result = await healthCheckService.checkHealth(true);
    if (DEBUG_VERBOSE_API) {
      console.log('[HealthCheck] Test result:', result);
    }
    return result;
  };
  if (DEBUG_VERBOSE_API) {
    console.log('[HealthCheck] Debug helpers: window.healthCheckService, window.testHealthCheck()');
  }
}

export default healthCheckService;